import React from 'react';
import { Modal, PasswordInput, Select, TextInput } from '@mantine/core';
import { Button } from '../../../components/Button';
import type { TenantChoice, UserFormState } from '../users-page.types';
import classes from '../users-page.module.scss';

type UserFormModalProps = {
  isOpen: boolean;
  isEditing: boolean;
  modalTitle: string;
  submitLabel: string;
  form: UserFormState;
  tenants: TenantChoice[];
  selectedTenantIds: string[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onChangeForm: <K extends keyof UserFormState>(key: K, value: UserFormState[K]) => void;
  onToggleTenant: (tenantId: string) => void;
};

export const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  isEditing,
  modalTitle,
  submitLabel,
  form,
  tenants,
  selectedTenantIds,
  isSubmitting,
  onClose,
  onSubmit,
  onChangeForm,
  onToggleTenant,
}) => {
  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={modalTitle}
      centered
      radius="md"
      size="lg"
      withinPortal
      closeOnClickOutside={false}
      trapFocus={false}
      closeButtonProps={{ 'data-testid': 'users-form-close' } as Record<string, string>}
    >
      <form onSubmit={onSubmit} className={classes.modalForm} noValidate>
        <div className={classes.modalRow}>
          <TextInput
            label="שם תצוגה"
            placeholder="שם תצוגה"
            value={form.displayName}
            onChange={(event) => onChangeForm('displayName', event.currentTarget.value)}
            minLength={2}
            maxLength={120}
            required
            size="md"
            radius="md"
            data-testid="users-form-display-name"
          />
          <TextInput
            label="שם משתמש"
            placeholder="שם משתמש"
            value={form.username}
            onChange={(event) => onChangeForm('username', event.currentTarget.value)}
            minLength={3}
            maxLength={25}
            required
            disabled={isEditing}
            size="md"
            radius="md"
            data-testid="users-form-username"
          />
        </div>
        <div className={classes.modalRow}>
          <TextInput
            label='דוא"ל'
            placeholder='דוא"ל'
            type="email"
            value={form.email}
            onChange={(event) => onChangeForm('email', event.currentTarget.value)}
            required
            disabled={isEditing}
            size="md"
            radius="md"
            data-testid="users-form-email"
          />
          <PasswordInput
            label={isEditing ? 'סיסמה חדשה (אופציונלי)' : 'סיסמה'}
            placeholder="סיסמה"
            value={form.password}
            onChange={(event) => onChangeForm('password', event.currentTarget.value)}
            minLength={isEditing ? undefined : 8}
            required={!isEditing}
            size="md"
            radius="md"
            data-testid="users-form-password"
          />
        </div>
        <Select
          label="תפקיד"
          value={form.role}
          onChange={(value) => onChangeForm('role', (value === 'user' ? 'user' : 'admin') as 'admin' | 'user')}
          data={[
            { value: 'admin', label: 'מנהל' },
            { value: 'user', label: 'משתמש' },
          ]}
          allowDeselect={false}
          size="md"
          radius="md"
          data-testid="users-form-role"
        />

        <fieldset className={classes.tenantsFieldset}>
          <legend className={classes.tenantsLegend}>גישה לחברות</legend>
          <div className={classes.tenantsList}>
            {tenants.map((tenant) => (
              <label key={tenant.id} className={classes.tenantChip}>
                <input
                  type="checkbox"
                  checked={selectedTenantIds.includes(tenant.tenantId)}
                  onChange={() => onToggleTenant(tenant.tenantId)}
                  data-testid={`users-form-tenant-${tenant.tenantId}`}
                />
                <span>{tenant.companyName}</span>
              </label>
            ))}
            {tenants.length === 0 && <span>אין חברות פעילות</span>}
          </div>
        </fieldset>

        <div className={classes.modalActions}>
          <Button variant="cancel" onClick={onClose} disabled={isSubmitting} dataTestId="users-form-cancel">
            ביטול
          </Button>
          <Button type="submit" variant="save" loading={isSubmitting} loadingLabel="שומר..." dataTestId="users-form-submit">
            {submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
